import {useCallback} from 'react'
import {Pressable, View} from 'react-native'
import {Image} from 'expo-image'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useNavigation} from '@react-navigation/native'

import {formatCount} from '#/view/com/util/numeric/format'
import {atoms as a, useTheme} from '#/alf'
import {useInteractionState} from '#/components/hooks/useInteractionState'
import {MediaInsetBorder} from '#/components/MediaInsetBorder'
import {Text} from '#/components/Typography'
import type {YouTubeSearchItem} from '#/state/queries/youtube-search'

/** Widescreen 16:9 – thumbnails not stretched */
const THUMB_ASPECT = 16 / 9

export function YouTubeVideoCard({item}: {item: YouTubeSearchItem}) {
  const t = useTheme()
  const {i18n} = useLingui()
  const navigation = useNavigation()
  const {
    state: pressed,
    onIn: onPressIn,
    onOut: onPressOut,
  } = useInteractionState()

  const onPress = useCallback(() => {
    navigation.navigate('YouTubeVideo', {
      videoId: item.id,
      title: item.title,
      channelTitle: item.channelTitle,
    })
  }, [navigation, item.id, item.title, item.channelTitle])

  const viewCountStr =
    item.viewCount != null
      ? formatCount(i18n, parseInt(item.viewCount, 10))
      : null

  return (
    <Pressable
      onPress={onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      accessibilityRole="link"
      accessibilityLabel={item.title}
      accessibilityHint="Plays video in app"
      style={[a.flex_col, t.atoms.shadow_sm]}>
      <View
        style={[
          a.rounded_lg,
          a.overflow_hidden,
          a.border,
          t.atoms.border_contrast_low,
          {
            backgroundColor: t.palette.black,
            aspectRatio: THUMB_ASPECT,
          },
        ]}>
        {item.thumbnail ? (
          <Image
            source={{uri: item.thumbnail}}
            style={[a.w_full, a.h_full, {opacity: pressed ? 0.85 : 1}]}
            contentFit="cover"
            accessibilityIgnoresInvertColors
          />
        ) : null}
        <MediaInsetBorder />
      </View>
      <View style={[a.p_sm, a.gap_2xs]}>
        <Text
          style={[a.text_sm, a.font_semi_bold, t.atoms.text]}
          numberOfLines={2}>
          {item.title}
        </Text>
        <View style={[a.flex_row, a.align_center, a.gap_sm, a.flex_wrap]}>
          <Text
            style={[a.text_xs, t.atoms.text_contrast_medium]}
            numberOfLines={1}>
            {item.channelTitle}
          </Text>
          {viewCountStr != null ? (
            <Text
              style={[a.text_xs, t.atoms.text_contrast_medium]}
              numberOfLines={1}>
              {viewCountStr} {i18n._(msg`views`)}
            </Text>
          ) : null}
        </View>
      </View>
    </Pressable>
  )
}
